import React from 'react';

export interface ActiveIconProps extends React.SVGProps<SVGSVGElement> {
  /** Width/height in px (defaults to 20). */
  size?: number | string;
  /** Optional accessible name; when provided the icon is exposed to assistive tech. */
  title?: string;
}

/**
 * Active/connected status glyph — a tick inside a rounded Bombiix-green chip.
 * Decorative by default (aria-hidden) since it sits beside a text status label.
 * @param props - component props (any valid SVG attribute may also be passed).
 * @returns the active status icon SVG element.
 */
function ActiveIcon({ size = 20, className = '', title, ...rest }: ActiveIconProps) {
  const accessibilityProps = title
    ? { role: 'img', 'aria-label': title }
    : { 'aria-hidden': true, focusable: false };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      {...accessibilityProps}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <rect width="20" height="20" rx="4" fill="var(--color-green-bg)" />
      <path
        d="M9.99967 16.6673C13.6663 16.6673 16.6663 13.6673 16.6663 10.0007C16.6663 6.33398 13.6663 3.33398 9.99967 3.33398C6.33301 3.33398 3.33301 6.33398 3.33301 10.0007C3.33301 13.6673 6.33301 16.6673 9.99967 16.6673Z"
        stroke="var(--color-green)"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7.16699 9.99995L9.05366 11.8866L12.8337 8.11328"
        stroke="var(--color-green)"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Pure presentational icon — memoised so it never re-renders unless its props change. */
export default React.memo(ActiveIcon);
